"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Image from "next/image";
import { getSlotDurationMinutes, isAM as isAMTime, hasEnoughCredits as checkCredits } from "@/utils/bookingUtils";
import { format } from "date-fns";
import { FaArrowDownLong } from "react-icons/fa6";
import { formatTime } from "@/utils/formatTime";
import { FaTimes, FaInfoCircle } from "react-icons/fa";

interface ConfirmBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherData: {
    name?: string;
  };
  selectedDay: Date | null;
  selectedTimeSlot: string;
  availability: {
    id: string;
    startTime: string;
    endTime: string;
  }[];
  handleConfirmBooking: () => void;
  calculateTotalCredits: () => number;
  isLoading?: boolean;
}

const ConfirmBookingModal: React.FC<ConfirmBookingModalProps> = ({
  isOpen,
  onClose,
  teacherData,
  selectedDay,
  selectedTimeSlot,
  availability,
  handleConfirmBooking,
  calculateTotalCredits,
  isLoading = false,
}) => {
  const selectedSlot = availability.find(
    (slot) => slot.id === selectedTimeSlot,
  );

  // Get available credits and required session duration
  const availableCredits = calculateTotalCredits();
  const requiredCredits = selectedSlot
    ? getSlotDurationMinutes(selectedSlot.startTime, selectedSlot.endTime)
    : 60;
  const hasEnoughCredits = checkCredits(availableCredits, requiredCredits);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg rounded-3xl border-8 border-[#27335D] bg-white p-0 [&>button:last-child]:hidden">
        {/* Header */}
        <div className="relative flex flex-col items-start justify-center rounded-t-xl bg-[#27335D] px-6 py-4 text-xl font-bold text-white">
          {/* Close Button (X) */}
          <button
            data-testid="confirmBookingModalCloseButton"
            onClick={onClose}
            className="cursor-pointer absolute right-4 top-4 text-2xl font-bold text-white transition hover:text-gray-300"
            aria-label="Close"
          >
            <FaTimes />
          </button>

          <div className="mx-auto text-center">
            <Image
              alt="Rawiah"
              src="/images/RawiahLogos/headerlogo.svg"
              width={100}
              height={100}
              unoptimized
              priority
              loading="eager"
              className="mb-3 w-24"
            />
            <h2 className="text-sm md:text-xl">
              Confirm your session with{" "}
              <span className="text-[#4887F5]">{teacherData?.name}</span>?
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Credits */}
          <p className="text-xl font-bold text-[#27335D]">
            Your Credits:{" "}
            <span
              data-testid="confirmBookingModalAvailableCredits"
              className={`${hasEnoughCredits ? "text-[#27335D]" : "text-[#c52f34]"}`}
            >
              {(availableCredits / 60).toFixed(1)} hours
            </span>
          </p>
          <p className="text-lg font-bold text-[#4887F5]">
            Required Credits:{" "}
            <span data-testid="confirmBookingModalRequiredCredits">
              {(requiredCredits / 60).toFixed(1)} hours
            </span>
          </p>

          {/* Day and Time Info */}
          <div className="mx-auto mb-8 mt-6 flex flex-col items-center justify-center md:mb-12 md:mt-10">
            {/* Month */}
            <div className="w-fit rounded-t-3xl border-2 border-b-0 border-[#27335D] bg-[#4887F5] px-5 py-1 text-sm font-bold text-[#27335D]">
              <span>{format(selectedDay || new Date(), "MMMM")}</span>
            </div>

            {/* Day */}
            <div className="w-48 rounded-t-3xl bg-[#27335d] px-12 py-3.5 text-center font-bold text-[#F6C65A] md:w-64">
              <div className="flex flex-row items-center justify-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-[#27335d]">
                  {format(selectedDay || new Date(), "d")}
                </div>
                <div className="text-sm font-bold text-white">
                  {format(selectedDay || new Date(), "EEEE")}
                </div>
              </div>
            </div>

            {/* Time */}
            <div className="flex w-48 justify-center rounded-b-3xl border-2 border-[#27335D] bg-white p-6 text-[#27335D] md:w-64">
              <div>
                {/* Start Time */}
                <p className="flex items-center gap-2 rounded-3xl border-2 border-[#27335d] bg-[#4887F5] px-3 py-2 text-sm font-medium text-[#27335d]">
                  <Image
                    src={`/images/svg/${
                      isAMTime(selectedSlot?.startTime || "0:00")
                        ? "sun"
                        : "moon2"
                    }.svg`}
                    alt="Time Icon"
                    width={10}
                    height={10}
                    className="h-4 w-4"
                  />
                  {formatTime(selectedSlot?.startTime || "00:00")}
                </p>

                <div className="my-2 flex flex-col items-center justify-center font-bold">
                  <FaArrowDownLong />
                </div>

                {/* End Time */}
                <p className="flex items-center gap-2 rounded-3xl border-2 border-[#27335d] bg-[#4887F5] px-3 py-2 text-sm font-medium text-[#27335d]">
                  <Image
                    src={`/images/svg/${
                      isAMTime(selectedSlot?.endTime || "0:00")
                        ? "sun"
                        : "moon2"
                    }.svg`}
                    alt="Time Icon"
                    width={10}
                    height={10}
                    className="h-4 w-4"
                  />
                  {formatTime(selectedSlot?.endTime || "00:00")}
                </p>
              </div>
            </div>

            <p className="mt-3 text-base font-bold text-[#4887F5] md:text-lg">
              {format(selectedDay || new Date(), "EEEE / MMM d / yyyy")}
            </p>
          </div>

          {/* 55-minute lesson note */}
          <p
            data-testid="confirmBookingLessonDurationNote"
            className="mb-6 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400"
          >
            <FaInfoCircle className="h-3 w-3 shrink-0" />
            Each hour slot is a 55-minute lesson, ending 5 minutes before the hour.
          </p>

          {/* Buttons */}
          <div className="flex flex-col items-center justify-center gap-2 md:flex-row">
            <button
              data-testid="confirmBookingModalConfirmButton"
              onClick={() => {
                handleConfirmBooking();
              }}
              disabled={!hasEnoughCredits || isLoading}
              className={`rounded-full border-b-[6px] border-[#4d4d4d] px-10 py-2 text-base font-bold md:text-xl ${
                hasEnoughCredits && !isLoading
                  ? "bg-[#27335D] text-[#E9E9EA] transition duration-150 ease-in-out hover:bg-[#525c7d] active:scale-95"
                  : "cursor-not-allowed border-gray-500 bg-gray-400 text-gray-300"
              }`}
            >
              {isLoading ? "Booking..." : "Confirm Booking"}
            </button>
            <button
              data-testid="confirmBookingModalCancelButton"
              onClick={onClose}
              className="rounded-full border-b-4 border-[#0c0f1c] bg-[#4887F5] px-10 py-2 text-base font-bold text-[#27335D] transition duration-150 ease-in-out hover:bg-[#6d9ff7] active:scale-95 md:text-xl"
            >
              Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmBookingModal;
